import React, { Component } from 'react';
import { connect } from 'react-redux';
import { setSearchFilters, getAllBenefits } from '../../redux/actions';
import * as routes from '../../constants/routes';
import {
    parseApiResponse,
    parseSearchFiltersFromUrl,
    clearSearchFilters,
    createPayloadFromSearchFilters,
    findCategoryById,
    combineCategoryRelevantBenefitTypes, cleanUpObject,
} from '../../app/functions';
import BreadCrumbs from '../../components/common/bread_crumbs';
import Loader from '../../components/common/loader';
import TopBanner from './sections/top_banner';
import Filters from './sections/filters';
import Results from './sections/results';
import './index.scss';

class Benefits extends Component {
    constructor(props) {
        super(props);

        this.state = {
            availableCategories: [],
            availableTags: [],
            tagsDefaultVisibility: false,
            shouldRequestData: false,
            waitingForInitialResponse: false,
        };

        this.initializeComponentState = this.initializeComponentState.bind(this);
        this.afterResponseFromAPI = this.afterResponseFromAPI.bind(this);
        this.getBenefits = this.getBenefits.bind(this);
        this.getBreadCrumbs = this.getBreadCrumbs.bind(this);
        this.getAvailableCategories = this.getAvailableCategories.bind(this);
        this.getAvailableTags = this.getAvailableTags.bind(this);
    }

    componentDidMount() {
        const { history, allBenefits } = this.props;

        let shouldRequestData = false;

        // If we get to the page by push action - get benefits
        if (history.action === 'PUSH') {
            shouldRequestData = true;
        }

        // If user clicked back but no benefits - get benefits
        if (history.action === 'POP' && allBenefits === false) {
            shouldRequestData = true;
        }

        this.initializeComponentState(shouldRequestData);
    }

    componentWillUnmount() {
        const { searchFilters, setSearchFilters } = this.props;

        setSearchFilters(
            clearSearchFilters(searchFilters)
        );
    }

    componentDidUpdate(prevProps) {
        const { searchFilters, pageNum, allBenefits } = this.props;

        let { shouldRequestData } = this.state;
        let shouldUpdateTags = false;

        // If there's new response from the API, update relevant state
        if (allBenefits !== prevProps.allBenefits) {
            this.afterResponseFromAPI();
        }

        // Send new request to the API if the filters/pageNum change
        if (searchFilters !== prevProps.searchFilters || pageNum !== prevProps.pageNum) {
            shouldRequestData = true;

            // If the category was changed, update availableTags as well
            if (searchFilters.category !== prevProps.searchFilters.category) {
                shouldUpdateTags = true;
            }
        }

        if (shouldRequestData) {
            this.getBenefits(shouldUpdateTags);
        }
    }

    initializeComponentState(shouldRequestData) {
        const { searchFilters, setSearchFilters } = this.props;

        const newFilters = parseSearchFiltersFromUrl(
            clearSearchFilters(searchFilters)
        );

        setSearchFilters(newFilters);

        this.setState({
            ...this.state,
            availableCategories: this.getAvailableCategories(),
            availableTags: this.getAvailableTags(),
            tagsDefaultVisibility: (newFilters.benefitType.id !== -1),
            shouldRequestData,
            waitingForInitialResponse: shouldRequestData,
        });
    }

    afterResponseFromAPI() {
        this.setState({
            waitingForInitialResponse: false,
        });
    }

    getBenefits(shouldUpdateTags) {
        const { searchFilters, pageNum, getAllBenefits } = this.props;

        const requestPayload = createPayloadFromSearchFilters(searchFilters, pageNum);

        getAllBenefits(requestPayload);

        const newState = {
            ...this.state,
            shouldRequestData: false,
        };

        if (shouldUpdateTags) {
            newState.availableTags = this.getAvailableTags();
        }

        this.setState(newState);
    }

    getBreadCrumbs() {
        const { categories, searchFilters } = this.props;

        const crumbs = [
            {
                text: 'כל ההטבות',
                route: routes.benefits,
            },
        ];

        // If there's a selected category, add it also to crumbs
        if (searchFilters.category.id !== -1) {
            const selectedCategory = findCategoryById(categories, searchFilters.category.id);

            if (selectedCategory) {
                crumbs.push({
                    text: selectedCategory.name,
                });
            }
        }

        return crumbs;
    }

    getAvailableCategories() {
        const { translations, categories } = this.props;

        const categoriesResult = [
            {
                id: -1,
                title: translations['all_benefits_all_categories'],
            },
        ];

        const apiCategories = parseApiResponse(
            categories, { id: 'number' }
        ).filter(
            (categoryData) => categoryData['allBenefitsOrderNum']
        ).sort(
            (a, b) => Number(a['allBenefitsOrderNum']) - Number(b['allBenefitsOrderNum'])
        );

        categoriesResult.push(...apiCategories);

        return categoriesResult;
    }

    getAvailableTags() {
        const { categories, benefitTypes, searchFilters } = this.props;

        const tagResults = [];

        // By default, show all benefit types
        let relevantBenefitTypes = Object.values(benefitTypes).filter(
            // Filter out benefit types that have been configured to be hidden from the all-benefits page
            (typeData) => Number(typeData.show_in_drop)
        ).map(
            // And keep only the IDs of the benefit types
            (typeData) => Number(typeData.id)
        );

        // If there's a selected category, show its benefit types instead
        if (searchFilters.category.id !== -1) {
            const selectedCategory = findCategoryById(categories, searchFilters.category.id);

            if (selectedCategory) {
                relevantBenefitTypes = combineCategoryRelevantBenefitTypes(selectedCategory);
            }
        }

        tagResults.push(
            ...parseApiResponse(
                Object.values(benefitTypes), { id: 'number' }
            ).filter(
                (tagData) => relevantBenefitTypes.includes(tagData.id)
            )
        );

        return tagResults;
    }

    render() {
        const { allBenefits } = this.props;
        const {
            availableCategories,
            availableTags,
            tagsDefaultVisibility,
            waitingForInitialResponse,
        } = this.state;

        return (
            <main className="benefits-page">
                <TopBanner />

                <BreadCrumbs
                    root={{
                        text: 'דף הבית',
                        route: routes.home,
                    }}
                    crumbs={this.getBreadCrumbs()}
                    className="benefits-breadcrumbs"
                />

                <div className="content-container">
                    {allBenefits && !waitingForInitialResponse ? (
                        <>
                            <Filters
                                availableCategories={availableCategories}
                                availableTags={availableTags}
                                tagsDefaultVisibility={tagsDefaultVisibility}
                            />

                            <Results data={allBenefits} />
                        </>
                    ) : (
                        <Loader className="data-loader" />
                    )}
                </div>
            </main>
        );
    }
}

const mapStateToProps = (store) => {
    return {
        translations: store.config.Translations,
        categories: cleanUpObject(store.config.categoriesArr).Data,
        benefitTypes: store.config.BenefitTypes,
        searchFilters: store.state.benefitSearchFilters,
        pageNum: store.state.searchPageNum,
        allBenefits: store.data.allBenefits,
    };
};

const mapDispatchToProps = (dispatch) => {
    return {
        setSearchFilters: (payload) => dispatch(setSearchFilters(payload)),
        getAllBenefits: (payload) => dispatch(getAllBenefits(payload)),
    };
};

export default connect(mapStateToProps, mapDispatchToProps, null, { pure: false })(Benefits);
